import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import MainLayout from '../components/layout/MainLayout';
import Section from '../components/ui/Section';
import Button from '../components/ui/Button';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';

const GuideDownload = () => {
  const [formData, setFormData] = useState({ 
    organisation: '',
    email: '',
    fullName: ''
  });
  const [status, setStatus] = useState('idle');
  const [session, setSession] = useState(null);
  const [guideUrl, setGuideUrl] = useState(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session?.user) {
        setFormData(prev => ({ 
          ...prev, 
          email: session.user.email,
          fullName: session.user.user_metadata?.full_name || ''
        }));
      }
    });

    const fetchGuideUrl = async () => {
      const { data } = await supabase
        .from('digital_products_1779580000000')
        .select('download_url')
        .eq('slug', 'responsible-leaders-guide')
        .single();
      if (data) setGuideUrl(data.download_url);
    };

    fetchGuideUrl();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');

    try {
      const { error } = await supabase
        .from('guide_downloads_1779668873179')
        .insert([{
          user_id: session?.user?.id || null,
          email: formData.email,
          organisation: formData.organisation,
          full_name: formData.fullName
        }]);

      if (error) throw error;
      setStatus('success');
    } catch (err) {
      console.error('Download error:', err);
      setStatus('error');
    }
  };

  return (
    <MainLayout>
      <Section bg="cream" className="pt-20 pb-24">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row gap-16 items-center">
          <div className="w-full md:w-1/2">
            <div className="relative">
              <div className="absolute -inset-4 bg-brand-teal/10 rounded-3xl -z-10"></div>
              <img 
                src="https://media-manager-c.questera.ai/greta-media/7c98abf852f963e06195176deed57bb9c81b4f53d205af5ca5b2f9af1b9cfaa840d35f6737e086b8644a3796bedcf0cc/images/aW1hZ2UvanBlZw==/a57c13e228f36e7ced2b38158dae4ce1.jpg" 
                alt="Responsible Leader's Guide Cover" 
                className="w-full h-auto rounded-xl shadow-2xl border border-gray-200"
              />
            </div>
          </div>

          <div className="w-full md:w-1/2">
            <h1 className="text-4xl font-bold text-brand-navy mb-6">
              The Responsible Leader’s Guide to AI
            </h1>
            
            {status === 'success' ? (
              <div className="bg-white p-10 rounded-2xl border-2 border-brand-teal shadow-xl animate-fade-in text-center">
                <div className="w-16 h-16 bg-brand-teal/10 text-brand-teal rounded-full flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={FiIcons.FiCheck} className="w-8 h-8" />
                </div>
                <h3 className="font-bold text-2xl text-brand-navy mb-4">Ready to Download</h3>
                <p className="text-gray-600 mb-8">
                  Thank you for your interest. You can access the guide directly below.
                </p>
                <Button 
                  href={guideUrl} 
                  variant="primary" 
                  fullWidth 
                  className="py-4 text-lg"
                >
                  <SafeIcon icon={FiIcons.FiDownload} className="mr-2" />
                  View Guide on Canva
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                <p className="text-xl text-gray-700 leading-relaxed">
                  A practical framework for nonprofit and cultural leaders to navigate AI with confidence.
                </p>

                {!session && (
                  <div className="bg-brand-teal/5 p-6 rounded-2xl border border-brand-teal/20 text-center">
                    <p className="text-brand-navy font-semibold mb-3">Already have an account?</p>
                    <Button to="/newsletter" variant="outline" className="bg-white text-sm py-2">
                      Sign In to Access Library
                    </Button>
                    <div className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-500">
                      <span className="h-px w-8 bg-gray-300"></span>
                      <span>OR DOWNLOAD AS GUEST</span>
                      <span className="h-px w-8 bg-gray-300"></span>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 space-y-5">
                  {!session && (
                    <>
                      <div className="text-left">
                        <label className="block text-sm font-bold text-brand-navy mb-1.5">Full Name</label>
                        <input 
                          type="text" 
                          required 
                          placeholder="Jane Doe"
                          value={formData.fullName}
                          onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                          className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-teal focus:border-brand-teal outline-none transition-all"
                        />
                      </div>
                      <div className="text-left">
                        <label className="block text-sm font-bold text-brand-navy mb-1.5">Work Email</label>
                        <input 
                          type="email" 
                          required 
                          placeholder="jane@organisation.org"
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-teal focus:border-brand-teal outline-none transition-all"
                        />
                      </div>
                    </>
                  )}
                  <div className="text-left">
                    <label className="block text-sm font-bold text-brand-navy mb-1.5">Your Organisation</label>
                    <input 
                      type="text" 
                      required 
                      placeholder="Charity or Company Name"
                      value={formData.organisation}
                      onChange={(e) => setFormData({...formData, organisation: e.target.value})}
                      className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-teal focus:border-brand-teal outline-none transition-all"
                    />
                  </div>

                  {status === 'error' && (
                    <p className="text-red-500 text-sm">Something went wrong. Please try again.</p>
                  )}

                  <Button 
                    type="submit" 
                    variant="primary" 
                    fullWidth 
                    className="py-4 text-lg"
                    disabled={status === 'submitting'}
                  >
                    {status === 'submitting' ? 'Preparing Download...' : 'Download Now'}
                  </Button>
                </form>
              </div>
            )}
          </div>
        </div>
      </Section>
    </MainLayout>
  );
};

export default GuideDownload;